import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../../theme";
import type { Choice, ChoiceLabel, SubmitResult } from "../types";

interface Props {
  choices: Choice[];
  selected: ChoiceLabel | null;
  result: SubmitResult | null;
  onPick: (label: ChoiceLabel) => void;
}

// Four full-width choice cards. Visual states (picked / correct /
// wrong) match the 2026 design refresh: 2px border + state-soft fill
// on each, padding shaved one px to preserve overall dimensions.
export function ChoiceList({
  choices,
  selected,
  result,
  onPick,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const locked = result !== null;
  return (
    <View
      style={styles.choices}
      accessibilityRole="radiogroup"
      accessibilityLabel="Answer choices"
    >
      {choices.map((c) => {
        const picked = selected === c.label;
        const isCorrect = locked && result.correctLabel === c.label;
        const isWrongPick =
          locked && picked && result.correctLabel !== c.label;
        return (
          <Pressable
            key={c.label}
            disabled={locked}
            onPress={() => onPick(c.label)}
            accessibilityRole="radio"
            accessibilityState={{ selected: picked, disabled: locked }}
            accessibilityLabel={`Choice ${c.label}: ${c.text}`}
            accessibilityHint={
              locked
                ? isCorrect
                  ? "Correct answer"
                  : isWrongPick
                    ? "Your answer, incorrect"
                    : undefined
                : "Double-tap to select this answer"
            }
            style={[
              styles.choice,
              picked && !locked && styles.choicePicked,
              isCorrect && styles.choiceCorrect,
              isWrongPick && styles.choiceWrong,
            ]}
          >
            <Text style={styles.choiceLabel}>{c.label}.</Text>
            <Text style={styles.choiceText}>{c.text}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    choices: { gap: space[2] },
    choice: {
      flexDirection: "row",
      gap: space[3],
      // Constant 2px border across all states so the picked/correct/wrong
      // states don't shift layout. Resting uses borderStrong (~3:1 contrast)
      // to delineate tap targets per WCAG 1.4.11.
      borderWidth: 2,
      borderColor: colors.borderStrong,
      borderRadius: radius.lg,
      padding: space[4],
      backgroundColor: colors.surface,
    },
    choicePicked: {
      borderColor: colors.borderPicked,
      backgroundColor: colors.surfacePicked,
    },
    choiceCorrect: {
      borderColor: colors.borderCorrect,
      backgroundColor: colors.surfaceCorrect,
    },
    choiceWrong: {
      borderColor: colors.borderWrong,
      backgroundColor: colors.surfaceWrong,
    },
    choiceLabel: {
      ...textStyle.buttonPrimary,
      color: colors.textPrimary,
      minWidth: 24,
    },
    choiceText: {
      ...textStyle.body,
      color: colors.textPrimary,
      flex: 1,
      fontSize: 15,
      lineHeight: 22,
      fontFamily: fontFamily.regular,
    },
  });
}
